/* Independent CACTOX modules: editor lifecycle, real-review pagination and shared anchors. */
(() => {
  'use strict';
  if (window.CurvilaCactoxSections) {
    window.CurvilaCactoxSections.init(document);
    return;
  }

  const selector = '[data-cc-module="reviews"], [data-cc-module="faq"]';
  const instances = new Map();
  let productObserver;
  let observedProduct;
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const behavior = () => reducedMotion() ? 'instant' : 'smooth';

  function formatCount(template, shown, total) {
    return String(template || '').replaceAll('[shown]', String(shown)).replaceAll('[total]', String(total));
  }

  function init(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const cards = Array.from(root.querySelectorAll('[data-cc-review]'));
    const pagination = root.querySelector('[data-cc-pagination]');
    const more = pagination?.querySelector('[data-cc-review-more]');
    const counter = pagination?.querySelector('[data-cc-review-count]');
    const requestedSize = Number(pagination?.dataset.pageSize);
    const pageSize = Number.isFinite(requestedSize) && requestedSize > 0 ? Math.floor(requestedSize) : 6;
    let shown = pagination ? Math.min(pageSize, cards.length) : cards.length;

    function render() {
      cards.forEach((card, index) => { card.hidden = index >= shown; });
      if (counter) counter.textContent = formatCount(pagination.dataset.countTemplate, shown, cards.length);
      if (more) more.hidden = shown >= cards.length;
    }

    function reveal(card) {
      const index = cards.indexOf(card);
      if (index < 0) return;
      shown = Math.min(cards.length, Math.max(shown, Math.ceil((index + 1) / pageSize) * pageSize));
      render();
    }

    more?.addEventListener('click', () => {
      const previous = shown;
      shown = Math.min(cards.length, shown + pageSize);
      render();
      cards[previous]?.focus({ preventScroll: true });
    }, { signal: abort.signal });

    instances.set(root, { abort, reveal, cards });
    render();
  }

  // Every target starts with its section-specific ID. The first matching target
  // receives the friendly hash; duplicates retain their collision-free ID.
  function syncAnchors() {
    const targets = Array.from(document.querySelectorAll('.cc-pdp [data-cc-anchor]'))
      .filter(node => !node.matches('a,button') && node.dataset.ccAnchor);
    targets.forEach(target => {
      if (!target.dataset.ccOriginalId) target.dataset.ccOriginalId = target.id;
      if (target.dataset.ccOriginalId) target.id = target.dataset.ccOriginalId;
    });
    targets.forEach(target => {
      const anchor = target.dataset.ccAnchor;
      const owner = document.getElementById(anchor);
      if (!owner || owner === target) target.id = anchor;
    });
  }

  function syncOffers() {
    const product = document.querySelector('[data-cc-product]');
    const enabled = product?.dataset.offersEnabled === 'true';
    document.querySelectorAll('.cc-pdp [data-cc-offer-content]').forEach(element => {
      element.hidden = element.dataset.ccOfferContent === 'enabled' ? !enabled : enabled;
    });
    if (product !== observedProduct) {
      productObserver?.disconnect();
      observedProduct = product;
      if (product) {
        productObserver = new MutationObserver(syncOffers);
        productObserver.observe(product, { attributes: true, attributeFilter: ['data-offers-enabled'] });
      }
    }
  }

  function initAll(scope = document) {
    if (scope.matches?.(selector)) init(scope);
    scope.querySelectorAll?.(selector).forEach(init);
    syncAnchors();
    syncOffers();
  }

  function destroy(scope) {
    for (const [root, instance] of instances) {
      if (root === scope || scope.contains?.(root)) {
        instance.abort.abort();
        instances.delete(root);
      }
    }
  }

  function resolveTarget(hash) {
    let id;
    try { id = decodeURIComponent(String(hash || '').replace(/^#/, '')); } catch { return null; }
    if (!id) return null;
    const exact = document.getElementById(id);
    if (exact) return exact;
    return Array.from(document.querySelectorAll('.cc-pdp [data-cc-anchor]'))
      .find(target => target.dataset.ccAnchor === id || target.dataset.ccOriginalId === id) || null;
  }

  function revealTarget(target, focus = false) {
    if (!target) return;
    if (target.matches('details')) target.open = true;
    let ancestor = target.parentElement;
    while (ancestor) {
      if (ancestor.matches('details')) ancestor.open = true;
      ancestor = ancestor.parentElement;
    }
    const card = target.closest('[data-cc-review]');
    if (card) {
      const instance = instances.get(card.closest(selector));
      instance?.reveal(card);
    }
    target.scrollIntoView({ behavior: behavior(), block: 'start' });
    if (focus) {
      const control = target.matches('details') ? target.querySelector('summary') : target;
      if (control && !control.hasAttribute('tabindex') && !control.matches('a,button,input,select,textarea,summary')) {
        control.setAttribute('tabindex', '-1');
        control.addEventListener('blur', () => control.removeAttribute('tabindex'), { once: true });
      }
      control?.focus({ preventScroll: true });
    }
  }

  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('a[href]');
    if (!link || link.target === '_blank' || link.hasAttribute('download') || !link.closest('.cc-pdp')) return;
    let url;
    try { url = new URL(link.href, window.location.href); } catch { return; }
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || !url.hash) return;
    const target = resolveTarget(url.hash);
    if (!target || !target.closest('.cc-pdp')) return;
    event.preventDefault();
    revealTarget(target, true);
    if (location.hash !== url.hash) history.pushState(null, '', url.hash);
  });

  document.addEventListener('shopify:block:select', event => {
    const blockId = event.detail?.blockId;
    const target = event.target.closest?.('[data-cc-review],[data-cc-faq-item]')
      || Array.from(document.querySelectorAll('[data-cc-review],[data-cc-faq-item]')).find(block => block.dataset.blockId === blockId);
    if (target) revealTarget(target);
  });
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    destroy(event.target);
    // Shopify emits unload before removing the old section node.
    window.setTimeout(() => { syncAnchors(); syncOffers(); }, 0);
  });
  document.addEventListener('shopify:section:reorder', () => { syncAnchors(); syncOffers(); });
  document.addEventListener('cc:product-ready', syncOffers);
  document.addEventListener('cc:offers-change', syncOffers);
  window.addEventListener('hashchange', () => revealTarget(resolveTarget(location.hash)));
  window.addEventListener('pageshow', () => { syncAnchors(); syncOffers(); });
  document.addEventListener('DOMContentLoaded', () => {
    initAll();
    if (location.hash) revealTarget(resolveTarget(location.hash));
  }, { once: true });
  window.CurvilaCactoxSections = { init: initAll, destroy, syncOffers, syncAnchors };
  if (document.readyState !== 'loading') {
    initAll();
    if (location.hash) revealTarget(resolveTarget(location.hash));
  }
})();
